/**
 * Shared model for the Knowledge Processing Console.
 *
 * Both console variants (2D canvas orbit and the three.js scene) read their
 * stages, palette and run timeline from here so the two stay in lockstep.
 */
import { useEffect, useRef, useState } from "react";
import {
  ScanText,
  ListTree,
  LayoutGrid,
  Sparkles,
  CheckCircle2,
  Share2,
  Network,
  Brain,
  Hexagon,
  FileCheck,
  CircleDot,
  Link2,
  AlignLeft,
  Star,
  Landmark,
  type LucideIcon,
} from "lucide-react";
import type { ValidateFileResult } from "../../../api/frameworksApi";

/* ── Palette ──
   The console uses the app's own theme tokens rather than a bespoke palette,
   so it follows light/dark with the rest of the dashboard. DOM styling uses
   Tailwind semantic classes (bg-card, border-border, text-foreground, ...) or
   `var(--chart-N)` for accents; canvas and WebGL need real values, so they read
   the computed custom properties through `readPalette()`. */
export const ACCENT_VARS = {
  primary: "var(--primary)",
  chart1: "var(--chart-1)",
  chart2: "var(--chart-2)",
  chart3: "var(--chart-3)",
  chart4: "var(--chart-4)",
  chart5: "var(--chart-5)",
} as const;

export interface ConsolePalette {
  background: string;
  card: string;
  border: string;
  foreground: string;
  mutedForeground: string;
  primary: string;
  chart: [string, string, string, string, string];
}

/** Light-mode fallbacks, used if a custom property cannot be read. */
const PALETTE_FALLBACK: ConsolePalette = {
  background: "#f5f7fb",
  card: "#ffffff",
  border: "#e2e8f0",
  foreground: "#0f172a",
  mutedForeground: "#475569",
  primary: "#4f46e5",
  chart: ["#4f46e5", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"],
};

/** Resolves the app's theme tokens to concrete colours for canvas/WebGL use. */
export function readPalette(): ConsolePalette {
  if (typeof window === "undefined") return PALETTE_FALLBACK;
  const s = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) =>
    s.getPropertyValue(name).trim() || fallback;
  return {
    background: v("--background", PALETTE_FALLBACK.background),
    card: v("--card", PALETTE_FALLBACK.card),
    border: v("--border", PALETTE_FALLBACK.border),
    foreground: v("--foreground", PALETTE_FALLBACK.foreground),
    mutedForeground: v("--muted-foreground", PALETTE_FALLBACK.mutedForeground),
    primary: v("--primary", PALETTE_FALLBACK.primary),
    chart: [
      v("--chart-1", PALETTE_FALLBACK.chart[0]),
      v("--chart-2", PALETTE_FALLBACK.chart[1]),
      v("--chart-3", PALETTE_FALLBACK.chart[2]),
      v("--chart-4", PALETTE_FALLBACK.chart[3]),
      v("--chart-5", PALETTE_FALLBACK.chart[4]),
    ],
  };
}

/** True when the app is in dark mode (the root carries `.dark`). */
export function isDarkTheme(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
}

/* ── Pipeline stages ── */
export interface PipelineStage {
  key: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  /** CSS custom property for this stage's accent, e.g. "var(--chart-1)". */
  accent: string;
  /** Index into ConsolePalette.chart, for canvas/WebGL use. */
  accentIndex: number;
  /** Time spent on this stage before advancing. `null` means hold here. */
  durationMs: number | null;
}

export const PIPELINE_STAGES: PipelineStage[] = [
  {
    key: "ingestion",
    label: "Ingestion",
    hint: "Ingesting the document",
    icon: ScanText,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
    durationMs: 1600,
  },
  {
    key: "structure",
    label: "Structure",
    hint: "Detecting headings, sections and tables",
    icon: ListTree,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
    durationMs: 1100,
  },
  {
    key: "chunk",
    label: "Chunk",
    hint: "Splitting the text into analysable passages",
    icon: LayoutGrid,
    accent: ACCENT_VARS.chart1,
    accentIndex: 0,
    durationMs: 900,
  },
  {
    key: "intelligence",
    label: "Intelligence extract",
    hint: "Extracting intelligence from the text",
    icon: Sparkles,
    accent: ACCENT_VARS.chart1,
    accentIndex: 0,
    durationMs: 1300,
  },
  {
    key: "semantic",
    label: "Semantic",
    hint: "AI-reviewed intent, risk and control alignment",
    icon: Brain,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
    durationMs: 1000,
  },
  {
    key: "validate",
    label: "Validate",
    hint: "The model is checking every rule against the text",
    icon: CheckCircle2,
    accent: ACCENT_VARS.chart4,
    accentIndex: 3,
    // Holds here until the real backend result lands.
    durationMs: null,
  },
  {
    key: "embed",
    label: "Embed",
    hint: "Generating vector embeddings",
    icon: Share2,
    accent: ACCENT_VARS.chart3,
    accentIndex: 2,
    durationMs: 700,
  },
  {
    key: "hub",
    label: "Hub",
    hint: "Publishing findings to the knowledge hub",
    icon: Network,
    accent: ACCENT_VARS.chart3,
    accentIndex: 2,
    durationMs: 550,
  },
];

/**
 * Index of the stage that waits on the real backend call.
 *
 * Two related concepts, deliberately kept separate:
 *   - `HOLD_STAGE_INDEX` (Validate) - where a *server-halted* job (PARTIAL /
 *     FAILED) is shown to stop in the pipeline strip, so downstream stages
 *     (Embed / Hub) don't paint green for a run that never got past the
 *     rule check.
 *   - `ANIMATION_HOLD_INDEX` (Structure) - where the *client animation*
 *     parks while /validate-file is in flight, so the strip doesn't fake
 *     progress through the LLM-heavy stages before we know the outcome.
 */
export const HOLD_STAGE_INDEX = PIPELINE_STAGES.findIndex(
  (s) => s.durationMs === null,
);

export const ANIMATION_HOLD_INDEX = PIPELINE_STAGES.findIndex(
  (s) => s.key === "structure",
);

/* ── Seven-layer validation ──
   The knowledge model each document is validated against. These orbit the
   RegO AI core and drive the "Seven-layer validation" panel. */
export interface ValidationLayer {
  key: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  /** CSS custom property for this layer's accent. */
  accent: string;
  /** Index into ConsolePalette.chart, for canvas/WebGL use. */
  accentIndex: number;
}

export const VALIDATION_LAYERS: ValidationLayer[] = [
  {
    key: "domain",
    label: "Domain",
    hint: "Subject areas the document governs",
    icon: Hexagon,
    accent: ACCENT_VARS.chart1,
    accentIndex: 0,
  },
  {
    key: "requirement",
    label: "Requirement",
    hint: "Obligations stated in the text",
    icon: FileCheck,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
  },
  {
    key: "concept",
    label: "Concept",
    hint: "Terms and definitions introduced",
    icon: CircleDot,
    accent: ACCENT_VARS.chart3,
    accentIndex: 2,
  },
  {
    key: "relationship",
    label: "Relationship",
    hint: "How requirements and risks connect",
    icon: Link2,
    accent: ACCENT_VARS.chart4,
    accentIndex: 3,
  },
  {
    key: "consistency",
    label: "Consistency",
    hint: "Internal contradictions and gaps",
    icon: AlignLeft,
    accent: ACCENT_VARS.chart5,
    accentIndex: 4,
  },
  {
    key: "quality",
    label: "Knowledge Quality",
    hint: "Clarity and completeness of what was extracted",
    icon: Star,
    accent: ACCENT_VARS.chart1,
    accentIndex: 0,
  },
  {
    key: "governance",
    label: "Governance",
    hint: "Ownership, approval and review controls",
    icon: Landmark,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
  },
];

/**
 * Console lifecycle phases.
 *
 * `uploading` and `running` are both in-flight states but map to different
 * animation holds:
 *   - `uploading` = POST /upload is in flight → animation parks at Structure
 *   - `running`   = POST /validate-file is in flight → animation parks at
 *                   Validate (if it reaches there before the API returns)
 *   - `complete`  = validate-file returned with a result
 *   - `error`     = either call failed and there's nothing more to run
 */
export type ConsolePhase = "uploading" | "running" | "complete" | "error";

/** Overall reading of a finished run, used to tint the centre visual. */
export type ConsoleTone = "neutral" | "pass" | "fail";

/**
 * How the run as a whole reads.
 *
 * Only a clean result is "pass" and only real failures are "fail". A run that
 * is still in flight, or one that came back with warnings but no failures,
 * stays neutral rather than being painted green or red on the user's behalf.
 */
export function buildResultTone(
  result: ValidateFileResult | null | undefined,
  phase: ConsolePhase,
): ConsoleTone {
  if (phase === "error") return "fail";
  if (!result) return "neutral";

  const job = String(result.processingJob?.status ?? "").toUpperCase();
  if (job === "FAILED" || job === "ERROR" || job === "PARTIAL") return "fail";
  if ((result.summary?.fail ?? 0) > 0) return "fail";
  if ((result.summary?.warn ?? 0) > 0) return "neutral";
  return "pass";
}

/* ── Run timeline ── */
export interface PipelineRun {
  /** Index of the stage currently lit. */
  stage: number;
  /** Every stage before this index is done. */
  done: boolean;
  /** Seconds since the run started. */
  elapsed: number;
  /** 0-100 overall progress. */
  progress: number;
  /** How many of the seven validation layers have been cleared. */
  layersLit: number;
  /**
   * Milliseconds the backend reported for this validation (ValidateFileResult
   * .durationMs), once it has returned. Null while the call is in flight.
   */
  apiDurationMs: number | null;
}

/** Formats a duration the way the console clock shows it, e.g. "1:04.8". */
export function formatDuration(ms: number): string {
  const total = ms / 1000;
  const m = Math.floor(total / 60);
  const s = total - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, "0")}`;
}

/**
 * Drives the pipeline animation.
 *
 * Stages 0..ANIMATION_HOLD_INDEX (Ingestion → Structure) advance on their
 * own short timers while the real /upload + /validate-file requests fly.
 * The run then parks on the Structure stage until `phase` leaves "running"
 * (the API returned or errored), then plays the remaining stages and
 * reports completion through `onFinished`. This keeps the strip honest -
 * it never shows Chunk/Intelligence/Semantic/Validate as green until the
 * backend has actually done that work.
 *
 * Pacing is anchored to real time rather than fixed constants wherever we have
 * it: `apiDurationMs` (ValidateFileResult.durationMs) scales the closing stages
 * and the seven-layer sweep, and the console clock switches from wall-clock to
 * the backend-reported duration once the call returns.
 */
export function usePipelineRun(
  phase: ConsolePhase,
  onFinished?: () => void,
  /** Duration the backend reported, once the call has returned. */
  apiDurationMs: number | null = null,
  /**
   * Layers the backend reported as cleared
   * (`sevenLayerValidation.clearedLayers`), once the call has returned. The
   * orbit and the layer panel settle on this instead of assuming all seven,
   * so a NOT_RUN result does not light up layers that never ran.
   */
  apiClearedLayers: number | null = null,
): PipelineRun {
  const [stage, setStage] = useState(0);
  const [done, setDone] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [layersLit, setLayersLit] = useState(0);
  const finishedRef = useRef(false);
  // Read through a ref so an inline callback from the caller cannot restart
  // the stage timer on every render and stall the run.
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  // Elapsed clock for the active stage badge.
  useEffect(() => {
    const start = Date.now();
    const t = setInterval(
      () => setElapsed(Math.floor((Date.now() - start) / 1000)),
      250,
    );
    return () => clearInterval(t);
  }, []);

  /* Once the API time is known, scale the closing stages to it: a fast
     validation should not sit through a fixed-length outro, and a slow one can
     afford a slightly longer one. Clamped so the pacing stays watchable. */
  const paceFactor =
    apiDurationMs === null
      ? 1
      : Math.min(1.6, Math.max(0.35, apiDurationMs / 6000));

  // Stage advance. One timer per stage keeps the dependency list minimal.
  useEffect(() => {
    // Two independent hold points, matched to the two API calls:
    //   - Park at Structure while /upload is in flight (phase="uploading")
    //   - Park at Validate while /validate-file is in flight (phase="running"),
    //     but only if the animation actually reached Validate first
    // The moment upload completes the store patches phase → "running",
    // which lifts the Structure hold and lets Chunk/Intelligence/Semantic
    // play through while validate-file is doing its work.
    const uploadingHold =
      stage >= ANIMATION_HOLD_INDEX && phase === "uploading";
    const validatingHold = stage >= HOLD_STAGE_INDEX && phase === "running";
    if (uploadingHold || validatingHold) return;

    const wait = (PIPELINE_STAGES[stage].durationMs ?? 600) * paceFactor;

    if (stage >= PIPELINE_STAGES.length - 1) {
      const t = setTimeout(() => {
        setDone(true);
        if (!finishedRef.current) {
          finishedRef.current = true;
          onFinishedRef.current?.();
        }
      }, wait);
      return () => clearTimeout(t);
    }

    const t = setTimeout(() => setStage((s) => s + 1), wait);
    return () => clearTimeout(t);
  }, [stage, phase, done, paceFactor]);

  // An error result stops the run where it stands.
  useEffect(() => {
    if (phase === "error") setDone(true);
  }, [phase]);

  /* Seven-layer validation is lit only from the backend's cleared count. The
     run does not sweep the layers on a timer: a simulated sweep turns layers
     green that the response then reports as never cleared, so the ring would
     flip from green to red the moment the result lands. Until the backend says
     a layer cleared, it has not. */
  useEffect(() => {
    setLayersLit(
      apiClearedLayers === null
        ? 0
        : Math.max(0, Math.min(apiClearedLayers, VALIDATION_LAYERS.length)),
    );
  }, [apiClearedLayers]);

  const progress = done
    ? 100
    : Math.round((stage / PIPELINE_STAGES.length) * 100);

  return { stage, done, elapsed, progress, layersLit, apiDurationMs };
}

/* ── Live yield, derived from the real validation result ── */
export interface ConsoleYield {
  label: string;
  value: string;
  hint: string;
  /** Matches the status palette used by ValidationResultPanel. */
  tone: "neutral" | "pass" | "warn" | "fail";
}

/** Value-text colours, aligned with ValidationResultPanel's status colours. */
export const YIELD_TONE_CLASS: Record<ConsoleYield["tone"], string> = {
  neutral: "text-indigo-600 dark:text-indigo-400",
  pass: "text-emerald-600 dark:text-emerald-400",
  warn: "text-amber-600 dark:text-amber-400",
  fail: "text-red-600 dark:text-red-400",
};

/**
 * Live yield tiles.
 *
 * The backend reports these directly in `liveYield`; that block wins whenever
 * it is present so the console shows the server's own counts. Responses that
 * omit it fall back to the rule summary.
 */
export function buildYield(
  result: ValidateFileResult | null,
  phase: ConsolePhase = "running",
): ConsoleYield[] {
  const s = result?.summary;
  const ly = result?.liveYield;
  const total = ly?.totalRules?.count ?? s?.total ?? 0;
  const passed = ly?.passed?.count ?? s?.pass ?? 0;
  /* Failed and warning are reported as one "attention" figure by the backend
     but are two different obligations to the user, so each gets its own tile. */
  const failed = ly?.failedAndWarning?.failedCount ?? s?.fail ?? 0;
  const warnings = ly?.failedAndWarning?.warningCount ?? s?.warn ?? 0;
  const rawScore =
    ly?.compliance?.percentage ??
    (ly?.compliance?.score !== undefined
      ? ly.compliance.score * 100
      : total > 0
        ? (passed / total) * 100
        : 0);
  // Whole-number percentage keeps the Compliance tile narrow so it doesn't
  // dwarf the other three tiles in the summary grid.
  const score = Math.round(rawScore);
  /* Three states drive the tiles:
     - errored: the request failed with no result → show em-dashes + a "not
       available" hint so the user isn't told the run is still in progress
       when it never will be
     - pending: request still in flight → em-dashes + "in progress"
     - settled: we have a result → show the real numbers */
  const errored = phase === "error" && !result;
  const pending = !errored && !result;
  const emptyHint = errored ? "not available" : pending ? "in progress" : "";
  const show = (n: number) => (result ? String(n) : "—");

  /* Number of rules that actually ran (skipped rules were not applicable
     to this document, so they don't contribute to compliance). Surfacing
     this on the Compliance tile prevents the "36 passed, 232 total, but
     82% compliance" confusion - the 82% is against the 50 applicable
     rules, not the full 232. */
  const skipped = s?.skipped ?? 0;
  const applicable = Math.max(0, total - skipped);
  const totalHint = result && skipped > 0 ? `${skipped} not applicable` : "";
  const applicableHint =
    result && total > 0 ? `of ${total} total rule${total === 1 ? "" : "s"}` : "";
  const complianceHint = result
    ? applicable > 0
      ? `${passed} of ${applicable} applicable rule${applicable === 1 ? "" : "s"} passed`
      : "no applicable rules"
    : errored
      ? "not available"
      : "";
  // `warnings` is captured above for the Compliance tone check even
  // though we no longer render a dedicated Warnings tile. Reference it
  // here so lint doesn't flag the variable as unused.
  void warnings;

  return [
    {
      label: "Total Rules",
      value: show(total),
      hint: totalHint || emptyHint,
      tone: "neutral",
    },
    /* Applicable = total - not-applicable. Made a first-class tile
       because the Compliance % is computed against this number, not
       against Total Rules - showing both side by side makes the
       denominator obvious at a glance. */
    {
      label: "Applicable",
      value: show(applicable),
      hint: applicableHint || emptyHint,
      tone: "neutral",
    },
    /* Compliance sits in the third slot so it lines up under the two
       counts it is calculated from (Total / Applicable). Failed drops
       below it - a smaller, secondary figure once the headline number
       is on screen. */
    {
      label: "Compliance",
      value: result ? `${score}%` : "—",
      hint: complianceHint,
      tone: !result ? "neutral" : score >= 80 ? "pass" : score >= 50 ? "warn" : "fail",
    },
    {
      label: "Failed",
      value: show(failed),
      hint: emptyHint,
      tone: "fail",
    },
  ];
}

/* ── Pipeline strip state, from the backend job ──
   `pipelineStages` carries per-stage progress when the backend reports it;
   `processingJob` carries the phase, the overall status and the last stage the
   job reached. Between them the strip can show what actually happened instead
   of the local animation - a PARTIAL or FAILED job must not paint Embed and
   Hub green when the run never got there. */
export type ConsoleStageState = "active" | "done" | "pending" | "failed";

/** Backend stage keys that name the same step as a console stage. */
const STAGE_KEY_ALIASES: Record<string, string> = {
  parse: "ingestion",
  ocr: "ingestion",
  ocr_parse: "ingestion",
  ingest: "ingestion",
  extract: "intelligence",
  intelligence_extract: "intelligence",
  publish: "hub",
};

function normalizeStageKey(key: string | undefined): string {
  const k = String(key ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s&-]+/g, "_");
  return STAGE_KEY_ALIASES[k] ?? k;
}

/**
 * Console stage states from the backend response, or null when it reports
 * neither per-stage progress nor a job - the caller then keeps the local run
 * animation.
 */
export function buildStageStates(
  result: ValidateFileResult | null,
): ConsoleStageState[] | null {
  const job = result?.processingJob;
  const apiStages = result?.pipelineStages ?? [];
  if (!job && apiStages.length === 0) return null;

  const byKey = new Map<string, string>();
  for (const s of apiStages) byKey.set(normalizeStageKey(s.key), String(s.status ?? ""));

  const jobStatus = String(job?.status ?? "").toUpperCase();
  const succeeded = jobStatus === "SUCCESS" || jobStatus === "COMPLETED";
  const halted = jobStatus === "PARTIAL" || jobStatus === "FAILED" || jobStatus === "ERROR";

  const currentIndex = PIPELINE_STAGES.findIndex(
    (s) => s.key === normalizeStageKey(job?.currentStage),
  );
  /* A halted job stops at Validate: nothing downstream of the rule check can
     have run, whatever stage the job reports as current. */
  const lastReached = halted
    ? Math.min(
        currentIndex >= 0 ? currentIndex : HOLD_STAGE_INDEX,
        HOLD_STAGE_INDEX,
      )
    : currentIndex;

  return PIPELINE_STAGES.map((stage, i) => {
    const reported = byKey.get(stage.key);
    if (reported) {
      const st = reported.trim().toLowerCase();
      if (["completed", "complete", "success", "passed", "done"].includes(st))
        return "done";
      if (["failed", "error"].includes(st)) return "failed";
      if (["running", "in_progress", "active"].includes(st)) return "active";
      return "pending";
    }
    if (succeeded) return "done";
    if (lastReached < 0) return "pending";
    if (i < lastReached) return "done";
    if (i === lastReached) return halted ? "failed" : "active";
    return "pending";
  });
}
